import { Avatar } from '@seek/avatar';
import type { Locale } from '@seek/melways-sites';
import { Box, Column, Columns, Stack } from 'braid-design-system';
import { observer } from 'mobx-react';
import type { ReactElement, FC } from 'react';

import { ProfileHeader } from 'src/App/AppPageBlock/ProfileDrawer/Profile/ProfileHeader';
import { hasVerifiedCredentials } from 'src/App/JobApplicationListSection/hasVerifiedCredentials';
import { mapToSimplifiedAnswers } from 'src/components/list/JobApplicationCard/RoleRequirementsView/RoleRequirement/mapToSimplifiedAnswers';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { PageConfig } from 'src/libs/PageConfig';
import type { IQuestionnaireStore } from 'src/stores/JobStore/QuestionnaireStore';

import type { IJobApplication } from '../../../../stores/JobApplicationStore/JobApplication';

import DetailsTabView from './DetailsTabView/DetailsTabView';
import transformProfileToComponentProps from './Profile/service/transformProfile';

import * as styles from './DetailsContentView.css';

interface Props {
  jobApplication: IJobApplication;
  changeStatusButtons: ReactElement;
  locale: Locale;
  questionnaireStore: IQuestionnaireStore;
  jobId: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  userId: string;
  onClickVerificationsTab: () => void;
  onClickVerificationsAlertLearnMore: () => void;
  onCloseVerificationsAlert: () => void;
  onProfileBodyVerificationsAlertMount: () => void;
  onClickVerificationsTabFooterLearnMore: () => void;
  onClickProfileBodyVerificationsTab: () => void;
  onClickProfileTabVerificationsLearnMore: () => void;
}
const DetailsContentView: FC<Props> = ({
  jobApplication,
  changeStatusButtons,
  locale,
  questionnaireStore,
  jobId,
  addCustomError,
  userId,
  onClickVerificationsTab,
  onClickVerificationsAlertLearnMore,
  onCloseVerificationsAlert,
  onProfileBodyVerificationsAlertMount,
  onClickVerificationsTabFooterLearnMore,
  onClickProfileBodyVerificationsTab,
  onClickProfileTabVerificationsLearnMore,
}: Props) => {
  const onClickRating = (selectedRatingValue: number): number => {
    if (selectedRatingValue === jobApplication.rating.rating) {
      const newRatingValue = 0;
      jobApplication.rating.updateRating(
        0,
        addCustomError,
        TRACKING_CANDIDATE_DETAILS,
      );
      return newRatingValue;
    }
    jobApplication.rating.updateRating(
      selectedRatingValue,
      addCustomError,
      TRACKING_CANDIDATE_DETAILS,
    );
    return selectedRatingValue;
  };

  const { language } = useAppConfig();

  const transformedProfile = jobApplication.profile
    ? // FIXME: we could look into removing this function call and do the mapping here
      transformProfileToComponentProps(jobApplication.profile)
    : null;

  const roleRequirements = mapToSimplifiedAnswers({
    questionnaire: questionnaireStore,
    roleRequirements: jobApplication.roleRequirements,
  });

  const shouldShowVerifiedCredentials = hasVerifiedCredentials({
    roleRequirements,
    profile: transformedProfile,
  });

  const shouldShowRelevantApplicationBadge =
    jobApplication.billableStatus === 'BILLABLE';

  const education =
    jobApplication.profile?.education?.length &&
    jobApplication.profile?.education?.length > 0
      ? jobApplication.profile?.education[0]
      : undefined;

  const certsyVerifiedFullName =
    jobApplication.profile?.verifiedIdentity?.fullName;

  return (
    <Columns space="small" align="left" alignY="top">
      <Column width="content" hideBelow="desktop">
        <Avatar
          variant="initials"
          name={jobApplication.fullName}
          size="large"
        />
      </Column>
      <Column>
        <Box background="surface" className={styles.cardContainer}>
          <Stack space="medium">
            <ProfileHeader
              candidateId={jobApplication.candidateId}
              jobApplicationId={jobApplication.id}
              fullName={jobApplication.fullName}
              certsyVerifiedFullName={certsyVerifiedFullName}
              role={{
                mostRecentJobTitle: jobApplication.mostRecentJobTitle,
                mostRecentCompanyName: jobApplication.mostRecentCompanyName,
              }}
              email={jobApplication.email}
              phone={jobApplication.phone}
              shouldShowVerifiedCredentials={shouldShowVerifiedCredentials}
              shouldShowRelevantApplicationBadge={
                shouldShowRelevantApplicationBadge
              }
              shouldShowNewBadge={jobApplication.isUnread}
              currentBucket={jobApplication.bucket}
              homeLocation={
                jobApplication.profile?.homeLocation?.description ?? ''
              }
              nationalities={transformedProfile?.nationalities}
              education={
                education
                  ? {
                      name: education.name.text,
                      institute: education.institute.text,
                      completionYear: education.completionDate?.year,
                    }
                  : undefined
              }
              appliedDate={jobApplication.appliedDate}
              source={jobApplication.source}
              rating={jobApplication.rating.rating}
              onClickRating={onClickRating}
              locale={locale}
              language={language}
              changeStatusButtons={changeStatusButtons}
            />
            <Box className={styles.cardContainer}>
              <DetailsTabView
                jobApplication={jobApplication}
                locale={locale}
                shouldShowVerifiedCredentials={shouldShowVerifiedCredentials}
                transformedProfile={transformedProfile}
                applicationQuestions={roleRequirements}
                jobId={jobId}
                addCustomError={addCustomError}
                userId={userId}
                onClickVerificationsAlertLearnMore={
                  onClickVerificationsAlertLearnMore
                }
                onCloseVerificationsAlert={onCloseVerificationsAlert}
                onProfileBodyVerificationsAlertMount={
                  onProfileBodyVerificationsAlertMount
                }
                onClickProfileBodyVerificationsTab={
                  onClickProfileBodyVerificationsTab
                }
                onClickVerificationsTabFooterLearnMore={
                  onClickVerificationsTabFooterLearnMore
                }
                onClickProfileTabVerificationsLearnMore={
                  onClickProfileTabVerificationsLearnMore
                }
                onClickVerificationsTab={onClickVerificationsTab}
              />
            </Box>
          </Stack>
        </Box>
      </Column>
    </Columns>
  );
};
export default observer(DetailsContentView);
